import createCommonModule from '@/common/store/modules/common';
import { getIpVersion } from '@/common/utils/ip';
import {
  STATE_ICON_RUN,
  STATUS_ICON_INACTIVE,
  STATUS_ICON_LOADING,
  STATUS_ICON_TIMES_O
} from '@/common/utils/statuses';
import {
  CREATING_STATUS,
  ACTIVE_STATUS,
  DISABLED_STATUS,
  CONFIGURING_STATUS,
  DELETING_STATUS,
  ERROR_STATUS,
  DEPLOYMENT_FAILED_STATUS,
  LOAD_BALANCER_POOL_MEMBER_STATUSES,
  FLAVOR_PROFILE_TOPOLOGY, LOAD_BALANCER_AMPHORAE_ROLE
} from '@/common/constants/loadBalancers.constants';
import i18n from '@/common/i18n';
import { every } from 'lodash-es';

const STATUS_ICONS = {
  [CREATING_STATUS]: STATUS_ICON_LOADING,
  [ACTIVE_STATUS]: STATE_ICON_RUN,
  [DISABLED_STATUS]: STATUS_ICON_INACTIVE,
  [CONFIGURING_STATUS]: STATUS_ICON_LOADING,
  [DELETING_STATUS]: STATUS_ICON_LOADING,
  [ERROR_STATUS]: STATUS_ICON_TIMES_O,
  [DEPLOYMENT_FAILED_STATUS]: STATUS_ICON_TIMES_O
};

const sumVmsByStat = (pools, stat) => pools.reduce((result, pool) => result + (pool.members?.filter(m => m.status === stat).length || 0), 0);

const getInitialState = () => ({
  flavors: [],
  flavorProfiles: []
});

export default createCommonModule({
  name: 'loadBalancers',
  resource: '/api/v2/compute/lbaas/loadbalancers',
  Model: Object,
  plural: ({ data }) => data,
  socketUpdates: true,
  state: getInitialState(),
  getters: {
    statusText: () => ({
      [CREATING_STATUS]: i18n.t('Creating'),
      [ACTIVE_STATUS]: i18n.t('Active'),
      [DISABLED_STATUS]: i18n.t('Disabled'),
      [CONFIGURING_STATUS]: i18n.t('Configuring'),
      [DELETING_STATUS]: i18n.t('Deleting'),
      [ERROR_STATUS]: i18n.t('Error'),
      [DEPLOYMENT_FAILED_STATUS]: i18n.t('Failed to create')
    }),
    getFlavors: (state) => (topology) => {
      const filteredProfiles = state.flavorProfiles.filter(profile => profile?.flavor_data?.loadbalancer_topology === topology);
      return state.flavors.filter(flavor => filteredProfiles?.some(profile => profile.id === flavor.flavor_profile_id));
    },
    getLoadBalancerTopologyByFlavorId: (state) => (flavorId) => {
      const flavorProfileId = state.flavors.find(flavor => flavor.id === flavorId)?.flavor_profile_id;
      const flavorProfile = (state.flavorProfiles.find(profile => profile.id === flavorProfileId));
      return flavorProfile?.flavor_data?.loadbalancer_topology;
    },
    getFlavorById: (state) => (id) => state.flavors?.find(flavor => flavor.id === id),
    isLbHaEnabled: (state, getters) => (lb) => {
      if (lb.flavor_id !== null) {
        const loadBalancerTopology = getters.getLoadBalancerTopologyByFlavorId(lb.flavor_id);
        return loadBalancerTopology === FLAVOR_PROFILE_TOPOLOGY.ACTIVE_STANDBY;
      }

      if (lb.amphorae?.find(it => it.role === LOAD_BALANCER_AMPHORAE_ROLE.STANDALONE)) {
        // standalone amphora is not HA
        return false;
      }

      if (lb.amphorae?.find(it => [LOAD_BALANCER_AMPHORAE_ROLE.MASTER, LOAD_BALANCER_AMPHORAE_ROLE.BACKUP].includes(it.role))) {
        // if there is at least one amphora with role MASTER or BACKUP, we assume that HA is enabled
        return true;
      }

      if (!!lb.amphorae && every(lb.amphorae, it => it.role === null)) {
        // when all amphorae have no role, we assume that HA is enabled if there are more than one amphorae
        return lb.amphorae.length > 1;
      }

      console.warn('Unknown HA status for load balancer: ', lb);
      return false;
    },
    _mapItem: (state, getters, rootState, rootGetters) => (id) => {
      const lb = state.itemsMap[id];
      if (!lb) {
        return null;
      }

      const stat = {
        [LOAD_BALANCER_POOL_MEMBER_STATUSES.HEALTHY]: sumVmsByStat(lb.pools, LOAD_BALANCER_POOL_MEMBER_STATUSES.HEALTHY),
        [LOAD_BALANCER_POOL_MEMBER_STATUSES.UNHEALTHY]: sumVmsByStat(lb.pools, LOAD_BALANCER_POOL_MEMBER_STATUSES.UNHEALTHY),
        [LOAD_BALANCER_POOL_MEMBER_STATUSES.ERROR]: sumVmsByStat(lb.pools, LOAD_BALANCER_POOL_MEMBER_STATUSES.ERROR),
        [LOAD_BALANCER_POOL_MEMBER_STATUSES.DISABLED]: sumVmsByStat(lb.pools, LOAD_BALANCER_POOL_MEMBER_STATUSES.DISABLED),
        [LOAD_BALANCER_POOL_MEMBER_STATUSES.UNKNOWN]: sumVmsByStat(lb.pools, LOAD_BALANCER_POOL_MEMBER_STATUSES.UNKNOWN)
      };

      const project = rootGetters['projects/getItemById']?.(lb.project_id);
      const isInProgress = [CREATING_STATUS, DELETING_STATUS].includes(lb.status);
      const amphorae = lb.amphorae?.length
        ? (lb.amphorae.find(it => it.active) || lb.amphorae[0])
        : {};
      const amphoraeCount = lb.amphorae?.length || 0;
      const flavor = getters.getFlavorById(lb.flavor_id);

      return {
        ...lb,
        ip_version: getIpVersion(lb.address) || 4,
        statusText: getters.statusText[lb.status] || lb.status,
        statusIcon: STATUS_ICONS[lb.status] || STATUS_ICONS.ERROR,
        stat,
        totalMembers: lb.members_count,
        isInProgress,
        computeId: amphorae.compute_id,
        projectName: project?.name,
        poolCount: lb.pools?.length,
        haEnabled: getters.isLbHaEnabled(lb),
        flavor,
        amphoraeCount
      };
    }
  },
  actions: {
    FETCH_FLAVORS({ commit }) {
      return this.$api.get('/api/v2/compute/lbaas/flavors')
        .then(res => {
          commit('SET_FLAVORS', res.data.data);
          return res;
        });
    },
    FETCH_FLAVOR_PROFILES({ commit }) {
      return this.$api.get('/api/v2/compute/lbaas/flavorprofiles')
        .then(res => {
          commit('SET_FLAVOR_PROFILES', res.data.data);
          return res;
        });
    }
  },
  mutations: {
    SET_FLAVORS(state, flavors) {
      state.flavors = flavors;
    },
    SET_FLAVOR_PROFILES(state, flavorProfiles) {
      state.flavorProfiles = flavorProfiles;
    }
  },
});
